import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, AlertTriangle, Sun, Zap, Gauge, BatteryLow } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { key: '24h', label: '24 h' },
  { key: '48h', label: '48 h' },
  { key: '7d', label: '7 days' },
];

const fmtTime = (t) =>
  new Date(t * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

function MiniChart({ title, unit, series, times, height = 150 }) {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const vals = series.flatMap((s) => s.points).filter((v) => typeof v === 'number');
  if (!vals.length) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="text-sm font-semibold mb-2">{title}</div>
        <div className="text-sm text-muted-foreground">No data yet.</div>
      </div>
    );
  }
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) max = min + 1;
  const pad = (max - min) * 0.12;
  min -= pad; max += pad;
  const W = 640, H = height;
  const n = Math.max(...series.map((s) => s.points.length), 2);
  const xAt = (i) => (i / (n - 1)) * W;
  const yAt = (v) => H - ((v - min) / (max - min)) * H;
  const path = (pts) => {
    let d = '';
    let started = false;
    pts.forEach((v, i) => {
      if (typeof v === 'number') {
        d += `${started ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)} `;
        started = true;
      }
    });
    return d.trim();
  };
  const firstIdx = series[0] ? series[0].points.findIndex((v) => typeof v === 'number') : 0;
  const areaPath = (pts) => {
    const line = path(pts);
    if (!line) return '';
    return `${line} L${W},${H} L${xAt(Math.max(firstIdx, 0)).toFixed(1)},${H} Z`;
  };

  const onMove = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    setHover({ idx: Math.round(ratio * (n - 1)), left: e.clientX - r.left });
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`history-chart-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
        <div className="text-sm font-semibold">{title}</div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          {series.map((s) => {
            const last = [...s.points].reverse().find((v) => typeof v === 'number');
            return (
              <span key={s.label} className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                {s.label}: <span className="font-medium text-foreground">{last != null ? `${last} ${unit}` : '—'}</span>
              </span>
            );
          })}
        </div>
      </div>
      <div ref={wrapRef} className="relative" onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ cursor: 'crosshair' }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
          <line x1="0" y1={H - 1} x2={W} y2={H - 1} stroke="currentColor" strokeOpacity="0.12" />
          {series.map((s, si) => (
            <React.Fragment key={si}>
              {series.length === 1 && <path d={areaPath(s.points)} fill={s.color} fillOpacity="0.12" />}
              <path d={path(s.points)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            </React.Fragment>
          ))}
          {hover && (
            <>
              <line x1={xAt(hover.idx)} y1="0" x2={xAt(hover.idx)} y2={H} stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
              {series.map((s, si) => typeof s.points[hover.idx] === 'number' && (
                <circle key={si} cx={xAt(hover.idx)} cy={yAt(s.points[hover.idx])} r="3.5" fill={s.color} />
              ))}
            </>
          )}
        </svg>
        {hover && times[hover.idx] != null && (
          <div
            className="pointer-events-none absolute top-1 z-10 rounded-md border border-border bg-popover px-2 py-1 text-xs shadow-md"
            style={{ left: Math.min(Math.max(hover.left - 60, 0), 999), transform: 'translateX(0)' }}
            data-testid="chart-tooltip"
          >
            <div className="text-muted-foreground mb-0.5">{fmtTime(times[hover.idx])}</div>
            {series.map((s) => (
              <div key={s.label} className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                {s.label}: <span className="font-medium">{s.points[hover.idx] != null ? `${s.points[hover.idx]} ${unit}` : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
        <span>max {max.toFixed(1)} {unit}</span>
        <span>min {min.toFixed(1)}</span>
      </div>
    </div>
  );
}

const SummaryCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Icon className={`h-5 w-5 ${accent}`} />
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  </div>
);

export function SolarHistory() {
  const [range, setRange] = useState('48h');
  const [samples, setSamples] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState('loading');

  const load = useCallback(async (r) => {
    try {
      const [h, s] = await Promise.all([
        axios.get(`${API}/solar/history?range=${r}`),
        axios.get(`${API}/solar/summary`),
      ]);
      setSamples(h.data.samples || []);
      setSummary(s.data);
      setStatus('ok');
    } catch (e) {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    setStatus('loading');
    load(range);
    const id = setInterval(() => load(range), 60000);
    return () => clearInterval(id);
  }, [range, load]);

  if (status === 'error') {
    return (
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-6 text-center" data-testid="history-error">
        <AlertTriangle className="h-5 w-5 mx-auto mb-2 text-amber-500" />
        History is unavailable right now — it will keep retrying.
      </div>
    );
  }

  const times = samples.map((s) => s.t);
  const col = (key) => samples.map((s) => s[key]);
  const chargerCount = samples.reduce((m, s) => Math.max(m, (s.chargers || []).length), 0);
  const hasDcLoad = samples.some((s) => typeof s.dcload === 'number');

  return (
    <div className="not-prose space-y-5" data-testid="solar-history">
      {/* Daily summary strip */}
      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="solar-summary">
          <SummaryCard icon={Sun} label="Generated Today" value={`${summary.generated_kwh} kWh`} accent="text-emerald-500" />
          <SummaryCard icon={Zap} label="Used Today" value={`${summary.used_kwh} kWh`} accent="text-amber-500" />
          <SummaryCard icon={Gauge} label="Peak Solar" value={`${summary.peak_solar_w} W`} accent="text-emerald-500" />
          <SummaryCard icon={BatteryLow} label="Min SOC" value={summary.min_soc != null ? `${summary.min_soc}%` : '—'} accent="text-sky-500" />
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="history-range">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
              className={`px-3 py-1.5 text-sm transition-colors ${range === r.key ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
              {r.label}
            </button>
          ))}
        </div>
        <button onClick={() => load(range)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="history-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} /> {samples.length} samples
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <MiniChart title="Battery SOC" unit="%" times={times} series={[{ label: 'SOC', color: '#0ea5e9', points: col('soc') }]} />
        <MiniChart title="Solar Power" unit="W" times={times} series={[{ label: 'PV', color: '#10b981', points: col('pv') }]} />
        <MiniChart title="Home Load" unit="W" times={times} series={[{ label: 'Load', color: '#f59e0b', points: col('load') }]} />
        {hasDcLoad && (
          <MiniChart title="Inverter DC Load" unit="W" times={times} series={[{ label: 'DC Load', color: '#8b5cf6', points: col('dcload') }]} />
        )}
        <MiniChart title="Battery Current" unit="A" times={times} series={[{ label: 'Battery', color: '#0ea5e9', points: col('batt_a') }]} />
        {Array.from({ length: chargerCount }, (_, i) => (
          <MiniChart key={i} title={`Charge Controller ${i + 1} Current`} unit="A" times={times}
            series={[{ label: `Charger ${i + 1}`, color: i === 0 ? '#10b981' : '#0ea5e9', points: samples.map((s) => (s.chargers || [])[i]) }]} />
        ))}
        <MiniChart title="Grid Power" unit="W" times={times} series={[{ label: 'Grid', color: '#ef4444', points: col('grid') }]} />
      </div>
      <p className="text-xs text-muted-foreground text-center">
        Hover any chart for the exact value &amp; time · updates every minute · 48h @1-min, 7-day @5-min.
      </p>
    </div>
  );
}
